/**
 * Shared helpers for the new multi-track NLE (web).
 * Keep catalog/constants in sync with mobile/src/utils/newNle.js and backend/utils/aiSoundEffects.js.
 */

export const NLE_MODE_KEY = 'nle_mode';
export const NLE_MODE_LEGACY = 'legacy';
export const NLE_MODE_NEW = 'new';

export const FLIPBOOK_INTRO_DURATION_SEC = 1.8;
export const FLIPBOOK_INTRO_CUT_COUNT = 15;
export const FLIPBOOK_INTRO_CUT_DURATION = 0.12;
export const NEW_NLE_PX_PER_SEC = 48;
/** Legacy lambda flipbook intro SFX (played in preview when Intro is on). */
export const RAPID_SHUTTER_URL = 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/sfx/rapidshutter.mp3';

export const SFX_BASE_URL = 'https://proto-filesharing.s3.eu-north-1.amazonaws.com/sfx/';

/** Catalog of placeable sound effects (mirrors backend SFX_CATALOG). */
export const SFX_CATALOG = [
  { id: 'awwww', file: 'awwww.m4a', label: 'Awww', when: 'Cute, wholesome, adorable moments' },
  { id: 'boom', file: 'boom.m4a', label: 'Boom', when: 'Shocking reveals, big dramatic hits' },
  { id: 'camera_shutter', file: 'camera shuter.m4a', label: 'Shutter', when: 'Camera, photo, filming' },
  { id: 'chaching', file: 'chaching.m4a', label: 'Cha-ching', when: 'Money, cash, revenue' },
  { id: 'chimes', file: 'Chimes.m4a', label: 'Chimes', when: 'Magical / satisfying moments' },
  { id: 'choc', file: 'choc.m4a', label: 'Choc', when: 'Short shock / surprise' },
  { id: 'click', file: 'click.m4a', label: 'Click', when: 'Numbered lists / steps' },
  { id: 'code', file: 'code.m4a', label: 'Code', when: 'Coding, software, apps' },
  { id: 'fairy_dust', file: 'Fairy dust.m4a', label: 'Fairy dust', when: 'Dreamy / whimsical' },
  { id: 'texting', file: 'iPhone Texting Sound Effect.mp3', label: 'Texting', when: 'SMS, DMs, messaging' },
  { id: 'keyboard', file: 'keyboard.m4a', label: 'Keyboard', when: 'Typing, writing' },
  { id: 'magic_chimes', file: 'Magic Chimes.mp3', label: 'Magic chimes', when: 'Enchanted beat' },
  { id: 'notification', file: 'notification.m4a', label: 'Notification', when: 'Alert / ping' },
  { id: 'party', file: 'party.m4a', label: 'Party', when: 'Celebration / success' },
  { id: 'riser', file: 'Riser Sound (An Actually Good One).mp3', label: 'Riser', when: 'Opening or before punchline' },
  { id: 'wow', file: 'wow.m4a', label: 'Wow', when: 'Impressed / amazed' },
  { id: 'yeay', file: 'yeay.m4a', label: 'Yeay', when: 'Happy cheer' },
  { id: 'nope', file: 'nope.m4a', label: 'Nope', when: 'Rejection / wrong idea' },
];

export const SFX_BY_ID = Object.fromEntries(SFX_CATALOG.map((s) => [s.id, s]));

/** Visual/mix fallback lengths (seconds). Overridden by decoded audio.duration when known. */
export const SFX_DURATION_SEC = {
  awwww: 1.2,
  boom: 1.4,
  camera_shutter: 0.4,
  chaching: 1.0,
  chimes: 1.5,
  choc: 0.5,
  click: 0.2,
  code: 0.8,
  fairy_dust: 1.8,
  texting: 1.0,
  keyboard: 1.2,
  magic_chimes: 1.6,
  notification: 0.6,
  party: 1.5,
  riser: 2.0,
  wow: 1.0,
  yeay: 1.0,
  nope: 0.8,
};

export function buildSfxUrl(file) {
  return `${SFX_BASE_URL}${encodeURIComponent(file)}`;
}

/** 360p short-GOP proxy for preview; never the HD export cut. */
export function cutPreviewUrl(cut) {
  const proxy = String(cut?.proxyUrl || cut?.playUrl || '').trim();
  if (proxy) return proxy;
  return String(cut?.url || '').trim();
}

export function cutPosterUrl(cut) {
  const poster = String(cut?.thumbnailUrl || cut?.thumbUrl || cut?.posterUrl || '').trim();
  return poster || null;
}

export function sfxCueDuration(cue) {
  const d = Number(cue?.duration);
  if (Number.isFinite(d) && d > 0.05) return Math.min(8, d);
  const mapped = SFX_DURATION_SEC[cue?.id];
  if (Number.isFinite(mapped) && mapped > 0.05) return mapped;
  return 0.8;
}

export function readNleMode() {
  try {
    const v = localStorage.getItem(NLE_MODE_KEY);
    return v === NLE_MODE_NEW ? NLE_MODE_NEW : NLE_MODE_LEGACY;
  } catch (_) {
    return NLE_MODE_LEGACY;
  }
}

export function writeNleMode(mode) {
  const next = mode === NLE_MODE_NEW ? NLE_MODE_NEW : NLE_MODE_LEGACY;
  try {
    localStorage.setItem(NLE_MODE_KEY, next);
  } catch (_) {}
  return next;
}

export function cutEffectiveSeconds(cut, fastPacing) {
  const d = typeof cut?.duration === 'number' && cut.duration >= 2 ? 2 : 1;
  return fastPacing && d === 2 ? 1 : d;
}

export function sumCutsSeconds(cuts, fastPacing) {
  return (cuts || []).reduce((acc, c) => acc + cutEffectiveSeconds(c, fastPacing), 0);
}

export function getVoiceoverSecondsFromTimestamps(timestamps) {
  const ends = timestamps?.character_end_times_seconds;
  if (!Array.isArray(ends) || !ends.length) return 0;
  const last = ends[ends.length - 1];
  return Number.isFinite(last) && last > 0 ? last : 0;
}

export function resolveRequiredSeconds({
  voiceoverSeconds = 0,
  flipbookIntro = false,
  isNoScriptMode = false,
  noScriptVideoLength = 15,
} = {}) {
  if (isNoScriptMode) return Math.max(1, Number(noScriptVideoLength) || 15);
  const vo = Math.max(0, Number(voiceoverSeconds) || 0);
  const flip = flipbookIntro ? FLIPBOOK_INTRO_DURATION_SEC : 0;
  return Math.max(0, Math.ceil(vo - flip));
}

/** Usage counts for cuts currently on the timeline (by cut id). */
export function cutUsageCounts(cuts) {
  const counts = new Map();
  for (const c of cuts || []) {
    const id = String(c?._id ?? c?.id ?? '');
    if (!id) continue;
    counts.set(id, (counts.get(id) || 0) + 1);
  }
  return counts;
}

export function cutPickerDurationLabel(cut, fastPacing) {
  if (fastPacing && typeof cut?.duration === 'number' && cut.duration >= 2) return '1 sec';
  if (typeof cut?.duration === 'number' && cut.duration >= 2) return '2 sec';
  return '1 sec';
}

/** Traditional random pick (mirrors lambda getRandomCuts). */
export function getRandomCuts(cuts, count) {
  const pool = cuts || [];
  if (!pool.length || !(count > 0)) return [];
  if (count <= pool.length) {
    return [...pool].sort(() => Math.random() - 0.5).slice(0, count);
  }
  const result = [];
  const fullCycles = Math.floor(count / pool.length);
  const remainder = count % pool.length;
  for (let cycle = 0; cycle < fullCycles; cycle++) {
    result.push(...[...pool].sort(() => Math.random() - 0.5));
  }
  if (remainder > 0) {
    result.push(...[...pool].sort(() => Math.random() - 0.5).slice(0, remainder));
  }
  return result;
}

/**
 * Pick cuts by selecting videos and taking up to 3 consecutive cuts
 * (mirrors lambda getRandomCutsFromVideos).
 */
export function getRandomCutsFromVideos(cuts, count, orderedVideoIds = null) {
  const preferOrder = Array.isArray(orderedVideoIds) && orderedVideoIds.length > 0;
  const cutsByVideo = {};
  for (const cut of cuts || []) {
    const videoId = cut.videoId != null ? String(cut.videoId) : null;
    if (!videoId) continue;
    if (!cutsByVideo[videoId]) cutsByVideo[videoId] = [];
    cutsByVideo[videoId].push(cut);
  }
  // Keep source order within each video when present
  for (const videoId of Object.keys(cutsByVideo)) {
    cutsByVideo[videoId].sort((a, b) => {
      const ai = Number(a.index ?? a.cutIndex ?? a.order ?? 0);
      const bi = Number(b.index ?? b.cutIndex ?? b.order ?? 0);
      return ai - bi;
    });
  }

  let originalVideoIds;
  if (preferOrder) {
    const seen = new Set();
    originalVideoIds = [];
    for (const id of orderedVideoIds.map(String)) {
      if (cutsByVideo[id] && !seen.has(id)) {
        originalVideoIds.push(id);
        seen.add(id);
      }
    }
    for (const id of Object.keys(cutsByVideo)) {
      if (!seen.has(id)) originalVideoIds.push(id);
    }
  } else {
    originalVideoIds = Object.keys(cutsByVideo);
  }

  if (!originalVideoIds.length) {
    return getRandomCuts(cuts, count);
  }

  const maxPossibleCuts = (cuts || []).length;
  if (maxPossibleCuts < count) {
    return getRandomCuts(cuts, maxPossibleCuts);
  }

  const estimatedVideosNeeded = Math.ceil(count / 3);
  const estimatedUniqueCuts = Math.min(estimatedVideosNeeded * 3, maxPossibleCuts);
  if (estimatedUniqueCuts > 90 && !preferOrder) {
    return getRandomCuts(cuts, count);
  }

  const result = [];
  const usedUrls = new Set();
  let videoIds = [...originalVideoIds];
  let resetCount = 0;
  const maxResets = 10;
  let iterationCount = 0;
  const maxIterations = count * 3;
  let orderCursor = 0;

  while (result.length < count && iterationCount < maxIterations) {
    iterationCount += 1;
    if (videoIds.length === 0) {
      if (resetCount >= maxResets) break;
      resetCount += 1;
      videoIds = [...originalVideoIds];
      orderCursor = 0;
      if (resetCount > 3 && result.length > 0 && result.length / resetCount < 1) {
        const remainingCount = count - result.length;
        const remainingCuts = (cuts || []).filter((cut) => !usedUrls.has(cut.url));
        result.push(...getRandomCuts(remainingCuts.length ? remainingCuts : cuts, remainingCount));
        break;
      }
      continue;
    }

    let pickIndex;
    if (preferOrder) {
      pickIndex = Math.min(orderCursor, videoIds.length - 1);
      orderCursor = (orderCursor + 1) % Math.max(1, videoIds.length);
    } else {
      pickIndex = Math.floor(Math.random() * videoIds.length);
    }
    const selectedVideoId = videoIds[pickIndex];
    const videoCuts = cutsByVideo[selectedVideoId] || [];
    const cutsToTake = Math.min(3, count - result.length, videoCuts.length);
    const startIndex = preferOrder
      ? 0
      : Math.floor(Math.random() * Math.max(1, videoCuts.length - cutsToTake + 1));

    for (let i = 0; i < cutsToTake; i++) {
      const cut = videoCuts[startIndex + i];
      if (!cut) continue;
      const key = cut.url || String(cut._id || cut.id || '');
      if (key && usedUrls.has(key)) continue;
      result.push(cut);
      if (key) usedUrls.add(key);
    }

    videoIds.splice(pickIndex, 1);
    if (preferOrder && orderCursor > pickIndex) orderCursor -= 1;
  }

  return result;
}

/** Same method choice as lambda voiceover path (~80% consecutive runs). */
export function pickCutsLikeLambda(availableCuts, count, orderedVideoIds = null) {
  const pool = (availableCuts || []).filter((c) => c && !c.hidden && (c.url || c.s3Key));
  if (!pool.length || !(count > 0)) return [];
  const hasVideoIds = pool.some((c) => c.videoId != null);
  if (hasVideoIds && count <= pool.length && Math.random() < 0.8) {
    const picked = getRandomCutsFromVideos(pool, count, orderedVideoIds);
    if (picked.length) return picked;
  }
  return getRandomCuts(pool, count);
}

/**
 * Auto-fill a timeline from a pool until required seconds are met.
 * Uses the same random + consecutive-video system as lambda.
 */
export function seedCutsToDuration(availableCuts, requiredSeconds, fastPacing, orderedVideoIds = null) {
  const pool = (availableCuts || []).filter((c) => c && !c.hidden && (c.url || c.s3Key));
  if (!pool.length || !(requiredSeconds > 0)) return [];

  const avgSec = pool.reduce((acc, c) => acc + cutEffectiveSeconds(c, fastPacing), 0) / pool.length;
  const estimate = Math.max(1, Math.ceil(requiredSeconds / Math.max(0.5, avgSec)));
  const out = pickCutsLikeLambda(pool, estimate, orderedVideoIds);
  let total = sumCutsSeconds(out, fastPacing);
  const max = Math.max(pool.length * 4, Math.ceil(requiredSeconds) + 8);

  while (total < requiredSeconds && out.length < max) {
    const more = pickCutsLikeLambda(pool, 1, orderedVideoIds);
    if (!more.length) break;
    out.push(more[0]);
    total += cutEffectiveSeconds(more[0], fastPacing);
  }
  return out;
}

/**
 * Append random cuts onto an existing timeline until required seconds are met.
 * Used so preview and export agree (no silent lambda-only fill).
 */
export function appendCutsToDuration(cuts, availableCuts, requiredSeconds, fastPacing, orderedVideoIds = null) {
  const current = Array.isArray(cuts) ? cuts.slice() : [];
  let total = sumCutsSeconds(current, fastPacing);
  if (!(requiredSeconds > 0) || total >= requiredSeconds) return current;
  const more = seedCutsToDuration(availableCuts, requiredSeconds - total, fastPacing, orderedVideoIds);
  return current.concat(more);
}

/** Persistent timeline-instance identity (survives reorder; unique per insertion). */
export function mintCutInstanceId(cut, i = 0) {
  const id = String(cut?._id ?? cut?.id ?? 'x');
  return `${id}-${Date.now()}-${i}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * Stamp `_nleKey` on a cut. Pass `force: true` when inserting a duplicate of an
 * existing asset so React/layout treat it as a new instance.
 */
export function stampCutKey(cut, i = 0, { force = false } = {}) {
  if (!force && cut?._nleKey) return cut;
  return { ...cut, _nleKey: mintCutInstanceId(cut, i) };
}

export function stampCutKeys(list, { force = false } = {}) {
  return (list || []).map((c, i) => stampCutKey(c, i, { force }));
}

export function cutInstanceId(cut, index = 0) {
  if (cut?._nleKey) return String(cut._nleKey);
  return `${String(cut?._id ?? cut?.id ?? 'x')}#${index}`;
}

/** Prefix pixel offsets for timeline cards (O(n) once per layout). */
export function buildCardPrefixOffsets(cardWidths, gapPx = 1) {
  const widths = cardWidths || [];
  const prefix = new Array(widths.length + 1);
  prefix[0] = 0;
  for (let i = 0; i < widths.length; i++) {
    prefix[i + 1] = prefix[i] + (widths[i] || 0) + gapPx;
  }
  return prefix;
}

/**
 * Pixel offset of item `index` given a reorder `positions` map and prefix table.
 * `slotPrefix[s]` = left edge of visual slot s when cards are in natural order widths.
 */
export function offsetForItemFromPrefix(index, positions, cardWidths, slotPrefix) {
  const usable = positions?.length === cardWidths?.length && slotPrefix?.length === (cardWidths?.length || 0) + 1;
  if (!usable) {
    let x = 0;
    const slot = index;
    for (let i = 0; i < (cardWidths || []).length; i++) {
      if (i < slot) x += (cardWidths[i] || 0) + 1;
    }
    return x;
  }
  const slot = positions[index];
  return slotPrefix[slot] || 0;
}

/** Words from ElevenLabs-style character timestamps. */
export function wordsFromVoiceoverTimestamps(voiceoverTimestamps) {
  const chars = voiceoverTimestamps?.characters;
  const starts = voiceoverTimestamps?.character_start_times_seconds;
  const ends = voiceoverTimestamps?.character_end_times_seconds;
  if (!Array.isArray(chars) || !Array.isArray(starts) || !Array.isArray(ends)) return [];

  const words = [];
  let current = '';
  let wordStart = null;
  let wordEnd = null;
  const isWs = (ch) => /\s/.test(ch);

  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];
    const s = starts[i];
    const e = ends[i];
    if (!isWs(ch)) {
      if (Number.isFinite(s) && wordStart == null) wordStart = s;
      if (Number.isFinite(e)) wordEnd = e;
      current += ch;
    }
    if ((isWs(ch) || i === chars.length - 1) && current.trim()) {
      words.push({
        text: current.trim(),
        start: Number.isFinite(wordStart) ? wordStart : words.length * 0.3,
        end: Number.isFinite(wordEnd) ? wordEnd : (Number.isFinite(wordStart) ? wordStart : 0) + 0.3,
      });
      current = '';
      wordStart = null;
      wordEnd = null;
    } else if (isWs(ch)) {
      current = '';
      wordStart = null;
      wordEnd = null;
    }
  }
  return words;
}

/** First sentence text from script (lambda opening-line match). */
export function extractFirstSentence(scriptContent) {
  const text = String(scriptContent || '').replace(/\s+/g, ' ').trim();
  if (!text) return '';
  const match = text.match(/^[^.!?]+[.!?]+/);
  return (match ? match[0] : text.split(/[.!?]/)[0] || text).trim();
}

/**
 * End time (sec) of the first sentence in the voiceover — title card duration.
 * Falls back to ~2.8s when timestamps are missing.
 */
export function firstSentenceEndSeconds(scriptContent, voiceoverTimestamps) {
  const firstSentence = extractFirstSentence(scriptContent);
  const words = wordsFromVoiceoverTimestamps(voiceoverTimestamps);
  if (!firstSentence || !words.length) {
    return words.length ? words[Math.min(words.length - 1, 8)].end : 2.8;
  }

  const expected = firstSentence.split(/\s+/).filter(Boolean);
  let sentenceWordIndex = 0;
  const matched = [];

  for (let i = 0; i < words.length; i++) {
    if (sentenceWordIndex >= expected.length) break;
    const normalizedWord = words[i].text.toLowerCase().replace(/[.,!?;:]/g, '');
    const normalizedExpected = expected[sentenceWordIndex].toLowerCase().replace(/[.,!?;:]/g, '');
    if (
      normalizedWord === normalizedExpected
      || normalizedWord.startsWith(normalizedExpected)
      || normalizedExpected.startsWith(normalizedWord)
    ) {
      matched.push(words[i]);
      sentenceWordIndex += 1;
      if (/[.!?]$/.test(words[i].text) || sentenceWordIndex >= expected.length) break;
    } else if (matched.length > 0 && /[.!?]$/.test(matched[matched.length - 1].text)) {
      break;
    }
  }

  if (matched.length) return matched[matched.length - 1].end;
  return Math.min(words[0].end + 2, words[Math.min(words.length - 1, 6)].end);
}

/** Timed words belonging to the first sentence (for title-card preview animation). */
export function firstSentenceTimedWords(scriptContent, voiceoverTimestamps) {
  const firstSentence = extractFirstSentence(scriptContent);
  const words = wordsFromVoiceoverTimestamps(voiceoverTimestamps);
  if (!words.length) {
    const fallback = String(firstSentence || '').split(/\s+/).filter(Boolean);
    return fallback.map((text, i) => ({
      text,
      start: i * 0.28,
      end: (i + 1) * 0.28,
    }));
  }
  if (!firstSentence) return words.slice(0, Math.min(words.length, 12));

  const expected = firstSentence.split(/\s+/).filter(Boolean);
  let sentenceWordIndex = 0;
  const matched = [];

  for (let i = 0; i < words.length; i++) {
    if (sentenceWordIndex >= expected.length) break;
    const normalizedWord = words[i].text.toLowerCase().replace(/[.,!?;:]/g, '');
    const normalizedExpected = expected[sentenceWordIndex].toLowerCase().replace(/[.,!?;:]/g, '');
    if (
      normalizedWord === normalizedExpected
      || normalizedWord.startsWith(normalizedExpected)
      || normalizedExpected.startsWith(normalizedWord)
    ) {
      matched.push(words[i]);
      sentenceWordIndex += 1;
      if (/[.!?]$/.test(words[i].text) || sentenceWordIndex >= expected.length) break;
    } else if (matched.length > 0 && /[.!?]$/.test(matched[matched.length - 1].text)) {
      break;
    }
  }
  return matched.length ? matched : words.slice(0, Math.min(words.length, 12));
}

/**
 * Resolve what's on screen at timeline time `timeSeconds`.
 * With flipbook intro: 0–1.8s = rapid cut flashes; VO/subs share that same clock from t=0.
 * Main cuts start at 1.8s.
 */
export function activeCutIndexAtTime(cuts, timeSeconds, fastPacing, flipbookIntro, flashPool) {
  let t = Math.max(0, Number(timeSeconds) || 0);
  if (flipbookIntro) {
    if (t < FLIPBOOK_INTRO_DURATION_SEC) {
      const flashIdx = Math.min(
        FLIPBOOK_INTRO_CUT_COUNT - 1,
        Math.floor(t / FLIPBOOK_INTRO_CUT_DURATION)
      );
      const pool = (flashPool && flashPool.length) ? flashPool : (cuts || []);
      const cut = pool.length ? pool[flashIdx % pool.length] : null;
      return {
        kind: 'intro',
        index: flashIdx,
        cut,
        localTime: (t % FLIPBOOK_INTRO_CUT_DURATION),
      };
    }
    t -= FLIPBOOK_INTRO_DURATION_SEC;
  }
  const list = cuts || [];
  if (!list.length) return { kind: 'none', index: null };
  let acc = 0;
  for (let i = 0; i < list.length; i++) {
    const end = acc + cutEffectiveSeconds(list[i], fastPacing);
    if (t < end) return { kind: 'cut', index: i, localTime: t - acc, cut: list[i] };
    acc = end;
  }
  return { kind: 'cut', index: list.length - 1, localTime: Math.max(0, cutEffectiveSeconds(list[list.length - 1], fastPacing) - 0.04), cut: list[list.length - 1] };
}

/** Stable key for a preview slot (cut identity on the timeline). */
export function previewClipKey(preview) {
  const url = cutPreviewUrl(preview?.cut);
  if (!url) return '';
  const instance = preview?.cut?._nleKey ? String(preview.cut._nleKey) : '';
  if (preview.kind === 'intro') return `intro:${preview.index}:${url}`;
  if (preview.kind === 'cut' && preview.index != null) {
    return instance ? `cut:${instance}:${url}` : `cut:${preview.index}:${url}`;
  }
  return instance ? `cut:${instance}:${url}` : `cut:${url}`;
}

/** Timeline end time of the active preview segment (absolute). */
export function previewSegmentEndTime(cuts, timeSeconds, fastPacing, flipbookIntro, flashPool) {
  const cur = activeCutIndexAtTime(cuts, timeSeconds, fastPacing, flipbookIntro, flashPool);
  const t = Math.max(0, Number(timeSeconds) || 0);
  if (cur.kind === 'intro') {
    return Math.min(
      FLIPBOOK_INTRO_DURATION_SEC,
      (cur.index + 1) * FLIPBOOK_INTRO_CUT_DURATION
    );
  }
  if (cur.kind !== 'cut' || cur.index == null) return t;
  let acc = flipbookIntro ? FLIPBOOK_INTRO_DURATION_SEC : 0;
  for (let i = 0; i <= cur.index; i++) {
    acc += cutEffectiveSeconds(cuts[i], fastPacing);
  }
  return acc;
}

/** The cut that will play immediately after the current segment (for A/B preroll). */
export function nextPreviewAtTime(cuts, timeSeconds, fastPacing, flipbookIntro, flashPool) {
  const end = previewSegmentEndTime(cuts, timeSeconds, fastPacing, flipbookIntro, flashPool);
  const next = activeCutIndexAtTime(cuts, end + 0.001, fastPacing, flipbookIntro, flashPool);
  const cur = activeCutIndexAtTime(cuts, timeSeconds, fastPacing, flipbookIntro, flashPool);
  if (!cutPreviewUrl(next?.cut)) return null;
  if (previewClipKey(next) === previewClipKey(cur)) return null;
  return { ...next, localTime: 0 };
}

/** Build subtitle blocks from character timestamps for the subs lane. */
export function buildSubtitleBlocks(scriptContent, voiceoverTimestamps, oneWordPerSubtitle = false, durationSec = 0) {
  const chars = voiceoverTimestamps?.characters;
  const starts = voiceoverTimestamps?.character_start_times_seconds;
  const ends = voiceoverTimestamps?.character_end_times_seconds;
  if (!Array.isArray(chars) || !Array.isArray(starts) || !Array.isArray(ends)) {
    const text = String(scriptContent || '').trim();
    if (!text) return [];
    const sentences = text.match(/[^.!?]+[.!?]+|[^.!?]+$/g)
      ?.map((s) => s.trim())
      .filter(Boolean)
      || [text];
    // Prefer short 1–2 line chunks (≈6 words). Never keep a whole long sentence as one block.
    const chunkWords = (sentence) => {
      const words = String(sentence).split(/\s+/).filter(Boolean);
      if (words.length <= 6) return [words.join(' ')];
      const out = [];
      for (let i = 0; i < words.length; i += 6) {
        out.push(words.slice(i, i + 6).join(' '));
      }
      return out;
    };
    const chunks = sentences.flatMap(chunkWords);
    const totalDur = Math.max(2, Number(durationSec) || chunks.length * 2.2);
    const slice = totalDur / Math.max(1, chunks.length);
    return chunks.map((chunk, i) => ({
      id: `sub-${i}`,
      text: chunk,
      start: i * slice,
      end: i === chunks.length - 1 ? totalDur : (i + 1) * slice,
    }));
  }

  const words = [];
  let current = '';
  let wordStart = null;
  let wordEnd = null;
  const isWs = (ch) => /\s/.test(ch);

  for (let i = 0; i < chars.length; i++) {
    const ch = chars[i];
    const s = starts[i];
    const e = ends[i];
    if (!isWs(ch)) {
      if (Number.isFinite(s) && wordStart == null) wordStart = s;
      if (Number.isFinite(e)) wordEnd = e;
      current += ch;
    }
    if ((isWs(ch) || i === chars.length - 1) && current.trim()) {
      words.push({
        text: current.trim(),
        start: Number.isFinite(wordStart) ? wordStart : words.length * 0.3,
        end: Number.isFinite(wordEnd) ? wordEnd : (Number.isFinite(wordStart) ? wordStart : 0) + 0.3,
      });
      current = '';
      wordStart = null;
      wordEnd = null;
    } else if (isWs(ch)) {
      current = '';
      wordStart = null;
      wordEnd = null;
    }
  }

  if (oneWordPerSubtitle) {
    return words.map((w, i) => ({ id: `sub-${i}`, text: w.text, start: w.start, end: w.end }));
  }

  const blocks = [];
  let buf = [];
  const flush = () => {
    if (!buf.length) return;
    blocks.push({
      id: `sub-${blocks.length}`,
      text: buf.map((w) => w.text).join(' '),
      start: buf[0].start,
      end: buf[buf.length - 1].end,
    });
    buf = [];
  };
  for (const w of words) {
    buf.push(w);
    if (buf.length >= 4 || /[.!?]$/.test(w.text)) flush();
  }
  flush();
  return blocks;
}

export function formatNleTime(seconds) {
  const s = Math.max(0, Math.floor(seconds || 0));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
}

/** Uniform px/sec timeline: scrollLeft maps 1:1 to time under a centered playhead. */
export function timelinePxFromTime(timeSec) {
  return Math.max(0, Number(timeSec) || 0) * NEW_NLE_PX_PER_SEC;
}

export function timelineTimeFromPx(px) {
  return Math.max(0, (Number(px) || 0) / NEW_NLE_PX_PER_SEC);
}

/**
 * Visible time window around the centered playhead, padded by `overscanPx` and snapped
 * outward to whole `bucketSec` buckets. Quantizing keeps the result stable across small
 * playhead moves, so memoized lanes only re-render when the window actually slides.
 * Keep in sync with mobile/src/utils/newNle.js.
 */
export function timelineWindowSec(playheadSec, viewportW, overscanPx, bucketSec = 4) {
  const half = ((Number(viewportW) || 0) / 2 + (Number(overscanPx) || 0)) / NEW_NLE_PX_PER_SEC;
  const t = Math.max(0, Number(playheadSec) || 0);
  return {
    fromSec: Math.max(0, Math.floor((t - half) / bucketSec) * bucketSec),
    toSec: Math.ceil((t + half) / bucketSec) * bucketSec,
  };
}

/**
 * Insert index for a new cut at the playhead (legacy half-cut rule).
 * Timeline time includes flipbook intro when enabled — cuts start after the intro.
 */
export function insertCutIndexAtPlayhead(cuts, timelineTime, fastPacing, flipbookIntro = false) {
  const intro = flipbookIntro ? FLIPBOOK_INTRO_DURATION_SEC : 0;
  const t = Math.max(0, Number(timelineTime) || 0);
  if (t <= intro) return 0;
  const cutTime = t - intro;
  const n = (cuts || []).length;
  if (n === 0) return 0;
  let acc = 0;
  for (let i = 0; i < n; i++) {
    const cutSec = cutEffectiveSeconds(cuts[i], fastPacing);
    const end = acc + cutSec;
    if (cutTime < end || i === n - 1) {
      const progress = cutSec > 0 ? Math.min(1, Math.max(0, (cutTime - acc) / cutSec)) : 0;
      return progress < 0.5 ? i : i + 1;
    }
    acc = end;
  }
  return n;
}

/**
 * Timeline time slightly past the middle of the cut at `cutIndex`, so the next
 * add-cut lands after that newly inserted cut. Includes flipbook intro offset.
 */
export function playheadJustPastMidCut(cuts, cutIndex, fastPacing, flipbookIntro = false) {
  const intro = flipbookIntro ? FLIPBOOK_INTRO_DURATION_SEC : 0;
  const list = cuts || [];
  if (!list.length || cutIndex < 0 || cutIndex >= list.length) return intro;
  let acc = 0;
  for (let i = 0; i < cutIndex; i++) {
    acc += cutEffectiveSeconds(list[i], fastPacing);
  }
  const cutSec = cutEffectiveSeconds(list[cutIndex], fastPacing);
  return intro + acc + cutSec * 0.55;
}

export function normalizeClientSfxCues(cues) {
  return (cues || [])
    .map((c, i) => {
      const meta = SFX_BY_ID[c.id] || SFX_BY_ID[c.sfx];
      const file = c.file || meta?.file;
      const url = c.url || (file ? buildSfxUrl(file) : null);
      const startTime = Number(c.startTime);
      if (!url || !Number.isFinite(startTime) || startTime < 0) return null;
      return {
        id: c.id || meta?.id || `sfx-${i}`,
        file: file || undefined,
        url,
        startTime,
        quote: typeof c.quote === 'string' ? c.quote : undefined,
        at: c.at === 'start' ? 'start' : 'word',
      };
    })
    .filter(Boolean);
}
